/**
 * Smart Wallet with Account Abstraction (ERC-4337)
 * Uses Coinbase Smart Wallet for gasless transactions via Paymaster
 */

import {
  createPublicClient,
  http,
  type Address,
  type Hex,
  type PublicClient,
  type Chain,
  encodeFunctionData,
} from 'viem';
import { baseSepolia, base } from 'viem/chains';
import CoinbaseWalletSDK from '@coinbase/wallet-sdk';
import { getChain, CONTRACTS, USDC_ABI, ESCROW_ABI, ENTRY_FEE } from './config.js';

// Smart wallet state
let smartWalletAddress: Address | null = null;
let publicClient: PublicClient | null = null;
let coinbaseWallet: CoinbaseWalletSDK | null = null;
let provider: any = null;
let currentChain: Chain | null = null;

// CDP Paymaster URL (from env)
function getPaymasterUrl(): string | undefined {
  if (typeof import.meta !== 'undefined' && (import.meta as any).env) {
    return (import.meta as any).env.VITE_PAYMASTER_URL;
  }
  return undefined;
}

/**
 * Initialize Coinbase Wallet SDK with Smart Wallet preference
 */
function initSmartWallet(): CoinbaseWalletSDK {
  if (!coinbaseWallet) {
    coinbaseWallet = new CoinbaseWalletSDK({
      appName: 'Mine or Die',
      appLogoUrl: '/logo.png',
    });
  }
  return coinbaseWallet;
}

/**
 * Check if smart wallet is connected
 */
export function isSmartWalletConnected(): boolean {
  return smartWalletAddress !== null;
}

/**
 * Get smart wallet address
 */
export function getSmartWalletAddress(): Address | null {
  return smartWalletAddress;
}

/**
 * Connect Smart Wallet
 * Coinbase Wallet SDK v4 automatically creates/uses Smart Wallet on Base
 */
export async function connectSmartWallet(): Promise<Address> {
  const chain = getChain();
  const sdk = initSmartWallet();

  // Create provider - SDK v4 uses Smart Wallet by default on Base
  provider = sdk.makeWeb3Provider();

  // Request accounts - will create Smart Wallet if needed
  const accounts = (await provider.request({
    method: 'eth_requestAccounts',
  })) as Address[];

  if (!accounts || accounts.length === 0) {
    throw new Error('No accounts returned from wallet');
  }

  smartWalletAddress = accounts[0];
  currentChain = chain;

  // Create public client for reads
  publicClient = createPublicClient({
    chain,
    transport: http(),
  });

  // Ensure correct chain
  try {
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: `0x${chain.id.toString(16)}` }],
    });
  } catch (switchError: any) {
    if (switchError.code === 4902) {
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainId: `0x${chain.id.toString(16)}`,
            chainName: chain.name,
            nativeCurrency: chain.nativeCurrency,
            rpcUrls: [chain.rpcUrls.default.http[0]],
            blockExplorerUrls: chain.blockExplorers
              ? [chain.blockExplorers.default.url]
              : undefined,
          },
        ],
      });
    }
  }

  console.log('Smart Wallet connected:', smartWalletAddress);
  return smartWalletAddress;
}

/**
 * Disconnect smart wallet
 */
export function disconnectSmartWallet(): void {
  smartWalletAddress = null;
  publicClient = null;
  provider = null;
  currentChain = null;
  console.log('Smart Wallet disconnected');
}

/**
 * Send a sponsored transaction via Paymaster
 * This allows gasless transactions for the user
 */
async function sendSponsoredTransaction(
  to: Address,
  data: Hex,
  value: bigint = 0n
): Promise<Hex> {
  if (!provider || !smartWalletAddress) {
    throw new Error('Smart wallet not connected');
  }

  const paymasterUrl = getPaymasterUrl();

  // Build transaction
  const tx: any = {
    from: smartWalletAddress,
    to,
    data,
    value: `0x${value.toString(16)}`,
  };

  // If paymaster is configured, use sponsored UserOperation
  if (paymasterUrl) {
    // Request paymaster sponsorship via wallet_sendCalls (EIP-5792)
    // Coinbase Smart Wallet supports this natively
    try {
      const calls = [{
        to,
        data,
        value: `0x${value.toString(16)}`,
      }];

      // wallet_sendCalls with paymaster capability
      const result = await provider.request({
        method: 'wallet_sendCalls',
        params: [{
          version: '1.0',
          from: smartWalletAddress,
          calls,
          capabilities: {
            paymasterService: {
              url: paymasterUrl,
            },
          },
        }],
      });

      console.log('Sponsored tx sent:', result);

      // wallet_sendCalls returns a batch ID, wait for it
      if (typeof result === 'string') {
        return result as Hex;
      }

      // If result is an object with id
      return (result as any).id || (result as any).hash;
    } catch (e: any) {
      console.warn('Paymaster sponsorship failed, falling back to regular tx:', e.message);
      // Fall through to regular transaction
    }
  }

  // Fallback: regular transaction (user pays gas)
  const hash = await provider.request({
    method: 'eth_sendTransaction',
    params: [tx],
  });

  return hash as Hex;
}

/**
 * Get USDC balance
 */
export async function getSmartWalletUSDCBalance(): Promise<bigint> {
  if (!publicClient || !smartWalletAddress || !CONTRACTS.usdc) {
    return 0n;
  }

  const balance = await publicClient.readContract({
    address: CONTRACTS.usdc,
    abi: USDC_ABI,
    functionName: 'balanceOf',
    args: [smartWalletAddress],
  });

  return balance;
}

/**
 * Approve USDC spending (sponsored)
 */
export async function approveUSDCSponsored(escrowAddress: Address): Promise<Hex> {
  if (!CONTRACTS.usdc) {
    throw new Error('USDC contract not configured');
  }

  const data = encodeFunctionData({
    abi: USDC_ABI,
    functionName: 'approve',
    args: [escrowAddress, ENTRY_FEE],
  });

  const hash = await sendSponsoredTransaction(CONTRACTS.usdc, data);
  console.log('Sponsored USDC approval tx:', hash);

  // Wait for confirmation
  if (publicClient) {
    await publicClient.waitForTransactionReceipt({ hash });
  }

  return hash;
}

/**
 * Check USDC allowance
 */
export async function getSmartWalletAllowance(escrowAddress: Address): Promise<bigint> {
  if (!publicClient || !smartWalletAddress || !CONTRACTS.usdc) {
    return 0n;
  }

  const allowance = await publicClient.readContract({
    address: CONTRACTS.usdc,
    abi: USDC_ABI,
    functionName: 'allowance',
    args: [smartWalletAddress, escrowAddress],
  });

  return allowance;
}

/**
 * Stake to match (sponsored)
 * Batches approve + stake into single sponsored transaction when possible
 */
export async function stakeToMatchSponsored(escrowAddress: Address): Promise<Hex> {
  if (!provider || !smartWalletAddress || !CONTRACTS.usdc) {
    throw new Error('Smart wallet not connected or USDC not configured');
  }

  const paymasterUrl = getPaymasterUrl();
  const allowance = await getSmartWalletAllowance(escrowAddress);

  // If we have paymaster and need approval, batch both calls
  if (paymasterUrl && allowance < ENTRY_FEE) {
    const approveData = encodeFunctionData({
      abi: USDC_ABI,
      functionName: 'approve',
      args: [escrowAddress, ENTRY_FEE],
    });

    const stakeData = encodeFunctionData({
      abi: ESCROW_ABI,
      functionName: 'stake',
      args: [],
    });

    try {
      // Batch approve + stake in single sponsored call
      const result = await provider.request({
        method: 'wallet_sendCalls',
        params: [{
          version: '1.0',
          from: smartWalletAddress,
          calls: [
            { to: CONTRACTS.usdc, data: approveData, value: '0x0' },
            { to: escrowAddress, data: stakeData, value: '0x0' },
          ],
          capabilities: {
            paymasterService: {
              url: paymasterUrl,
            },
          },
        }],
      });

      console.log('Sponsored batch (approve + stake) tx:', result);
      const hash = typeof result === 'string' ? result : (result as any).id || (result as any).hash;

      if (publicClient) {
        await publicClient.waitForTransactionReceipt({ hash });
      }

      return hash as Hex;
    } catch (e: any) {
      console.warn('Batch sponsorship failed:', e.message);
      // Fall through to sequential
    }
  }

  // Sequential fallback
  if (allowance < ENTRY_FEE) {
    console.log('Insufficient allowance, approving USDC...');
    await approveUSDCSponsored(escrowAddress);
  }

  const stakeData = encodeFunctionData({
    abi: ESCROW_ABI,
    functionName: 'stake',
    args: [],
  });

  const hash = await sendSponsoredTransaction(escrowAddress, stakeData);
  console.log('Sponsored stake tx:', hash);

  if (publicClient) {
    await publicClient.waitForTransactionReceipt({ hash });
  }

  return hash;
}

/**
 * Claim rewards (sponsored)
 */
export async function claimRewardsSponsored(escrowAddress: Address): Promise<Hex> {
  const data = encodeFunctionData({
    abi: ESCROW_ABI,
    functionName: 'claim',
    args: [],
  });

  const hash = await sendSponsoredTransaction(escrowAddress, data);
  console.log('Sponsored claim tx:', hash);

  if (publicClient) {
    await publicClient.waitForTransactionReceipt({ hash });
  }

  return hash;
}

/**
 * Check if player has staked
 */
export async function hasSmartWalletStaked(escrowAddress: Address): Promise<boolean> {
  if (!publicClient || !smartWalletAddress) {
    return false;
  }

  const staked = await publicClient.readContract({
    address: escrowAddress,
    abi: ESCROW_ABI,
    functionName: 'hasStaked',
    args: [smartWalletAddress],
  });

  return staked;
}

/**
 * Get claimable reward
 */
export async function getSmartWalletClaimableReward(escrowAddress: Address): Promise<bigint> {
  if (!publicClient || !smartWalletAddress) {
    return 0n;
  }

  const reward = await publicClient.readContract({
    address: escrowAddress,
    abi: ESCROW_ABI,
    functionName: 'claimableReward',
    args: [smartWalletAddress],
  });

  return reward;
}

/**
 * Check if gas sponsorship is available
 */
export function isGasSponsorshipAvailable(): boolean {
  return !!getPaymasterUrl();
}
